import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { AccountSettings } from '../../components/profile/AccountSettings.tsx';
import { BillingSettings } from '../../components/profile/BillingSettings.tsx';
import { PasswordReset } from '../../components/profile/PasswordReset.tsx';
import { PlansSubscriptions } from '../../components/profile/PlansSubscriptions.tsx';
import { ProjectSettings } from '../../components/profile/ProjectSettings.tsx';
import { SettingsSidebar } from '../../components/profile/SettingsSidebar.tsx';
import { UISettings } from '../../components/profile/UISettings.tsx';
import Footer from '../../components/projects/Footer';
import { useAppDispatch, useAppSelector } from '../../hooks.ts';
import { getInitials } from '../../services/util';
import { subscriptionReducer } from '../../store/reducers/subscription.reducer.ts';
import { toastReducer } from '../../store/reducers/toast.reducer.ts';
import { userReducer } from '../../store/reducers/user.reducer.ts';
import { getProfilePictureUrl, getUser } from '../../store/selectors/user.selector.ts';
import { BakeQuality, UpdateUserRequest } from '../../types/user.ts';
import { UnitSystem } from '../../types/util.ts';

const { updateUser, updateUserPassword, refreshUser } = userReducer.actions;
const { loadSubscription } = subscriptionReducer.actions;
const { showToast } = toastReducer.actions;

/** Sections that can be opened by link (`/profile?section=...`), e.g. when returning from a checkout. */
const LINKABLE_SECTIONS: Record<string, string> = {
  plans: 'Plans & Subscriptions',
  billing: 'Billing',
};

/** After a checkout the payment provider reports the purchase to the backend on its own, usually
 *  within seconds of sending the user back. Until then the plan and credits are still the old ones. */
const PURCHASE_REFRESH_DELAYS_MS = [0, 2000, 5000, 10000];

export const ProfilePage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSettingsSection, setActiveSettingsSection] = useState<string>(
    LINKABLE_SECTIONS[searchParams.get('section') ?? ''] ?? 'Account',
  );

  // Returning from the payment provider's checkout.
  const checkoutOutcome = searchParams.get('checkout');
  useEffect(() => {
    if (!checkoutOutcome) return;

    // Drop the outcome from the URL so a reload does not announce it again.
    setSearchParams(
      (params) => {
        params.delete('checkout');
        return params;
      },
      { replace: true },
    );

    if (checkoutOutcome !== 'success') {
      dispatch(showToast({ message: 'settings.billing.checkoutCancelled', type: 'info' }));
      return;
    }
    dispatch(showToast({ message: 'settings.billing.checkoutSuccess', type: 'success' }));
    PURCHASE_REFRESH_DELAYS_MS.forEach((delay) =>
      window.setTimeout(() => {
        dispatch(loadSubscription());
        dispatch(refreshUser());
      }, delay),
    );
  }, [checkoutOutcome, dispatch, setSearchParams]);

  const profilePictureUrl = useAppSelector(getProfilePictureUrl) || '';

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    units: '',
    tolerance: 0,
    maintenanceFactor: 0,
    bakeQuality: 'PRODUCTION',
    hideExampleProjects: false,
    measurementPointsOnHover: false,
    muteNotificationSound: false,
    profilePictureUrl: profilePictureUrl,
    profilePictureBase64: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName || '',
        email: user.email || '',
        units: user.settings?.units?.toString() || '',
        tolerance: user.settings.tolerance,
        maintenanceFactor: user.settings.maintenance,
        bakeQuality: user.settings.bakeQuality || 'PRODUCTION',
        hideExampleProjects: user.settings.hideExampleProjects || false,
        measurementPointsOnHover: user.settings.measurementPointsOnHover || false,
        muteNotificationSound: user.settings.muteNotificationSound || false,
        profilePictureUrl: profilePictureUrl,
        profilePictureBase64: '',
      });
    }
  }, [profilePictureUrl, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate required fields
      if (formData.fullName && !formData.fullName.trim()) {
        throw new Error(t('settings.account.fullNameRequired'));
      }

      const request: UpdateUserRequest = {
        fullName: formData.fullName,
        maintenance: formData.maintenanceFactor,
        profilePictureBase64: formData.profilePictureBase64,
        tolerance: formData.tolerance,
        units: formData.units.toUpperCase() as UnitSystem,
        bakeQuality: formData.bakeQuality as BakeQuality,
        hideExampleProjects: formData.hideExampleProjects,
        measurementPointsOnHover: formData.measurementPointsOnHover,
        muteNotificationSound: formData.muteNotificationSound,
      };
      dispatch(updateUser(request));
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (passwords: { newPassword: string; confirmPassword: string }) => {
    setIsSubmitting(true);

    try {
      dispatch(updateUserPassword(passwords));
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProfilePictureChange = (displayUrl: string, base64String?: string) => {
    setFormData((prev) => ({
      ...prev,
      profilePictureUrl: displayUrl,
      profilePictureBase64: base64String || '',
    }));

    // Clean up the object URL when no longer needed
    return () => {
      if (displayUrl.startsWith('blob:')) {
        URL.revokeObjectURL(displayUrl);
      }
    };
  };

  const isFormValid = formData.fullName && formData.fullName.trim() !== '';

  const handleSettingsSectionChange = (section: string) => {
    setActiveSettingsSection(section);
    // Keep the link in step with what is shown, so a reload stays on the same section.
    const linkName = Object.keys(LINKABLE_SECTIONS).find((name) => LINKABLE_SECTIONS[name] === section);
    setSearchParams(linkName ? { section: linkName } : {}, { replace: true });
  };

  return (
    <div className="family-inter">
      <div className="flex flex-col">
        <div className="flex flex-col md:flex-row overflow-hidden md:h-[calc(100vh-112px)]">
          <SettingsSidebar activeSection={activeSettingsSection} onSectionChange={handleSettingsSectionChange} />

          {activeSettingsSection === 'Account' && (
            <div className="flex-1 overflow-y-auto bg-black" style={{ minHeight: '0' }}>
              <AccountSettings
                fullName={formData.fullName}
                email={formData.email}
                profilePictureUrl={formData.profilePictureUrl}
                onNameChange={(name) => setFormData((prev) => ({ ...prev, fullName: name }))}
                onProfilePictureChange={handleProfilePictureChange}
                getInitials={getInitials}
              />

              <div className="flex h-[100px] p-8">
                <div className="">
                  <button
                    key={`edit-button`}
                    onClick={handleSubmit}
                    disabled={isSubmitting || !isFormValid}
                    className={`flex items-center justify-between px-6 py-3 rounded-full font-medium transition-colors duration-200 text-xs bg-green01 text-black hover:bg-green02 ${
                      isSubmitting || !isFormValid
                        ? 'bg-zinc-600 text-zinc-400 cursor-not-allowed'
                        : 'text-black hover:bg-green01'
                    }`}
                    style={{
                      minWidth: '70px',
                      height: '36px',
                    }}
                  >
                    <span>{isSubmitting ? t('common.saving') : t('common.save')}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeSettingsSection === 'Plans & Subscriptions' && <PlansSubscriptions />}

          {activeSettingsSection === 'Project' && (
            <div className="flex-1 overflow-y-auto bg-black" style={{ minHeight: '0' }}>
              <ProjectSettings
                units={formData.units as 'metric' | 'imperial'}
                tolerance={formData.tolerance}
                maintenanceFactor={formData.maintenanceFactor}
                bakeQuality={formData.bakeQuality}
                onUnitsChange={(units) => setFormData((prev) => ({ ...prev, units }))}
                onToleranceChange={(tolerance) => setFormData((prev) => ({ ...prev, tolerance }))}
                onMaintenanceFactorChange={(factor) => setFormData((prev) => ({ ...prev, maintenanceFactor: factor }))}
                onBakeQualityChange={(bakeQuality) => setFormData((prev) => ({ ...prev, bakeQuality }))}
              />

              <div className="flex h-[100px] p-8">
                <div className="">
                  <button
                    key={`edit-button`}
                    onClick={handleSubmit}
                    disabled={isSubmitting || !isFormValid}
                    className={`flex items-center justify-between px-6 py-3 rounded-full font-medium transition-colors duration-200 text-xs bg-green01 text-black hover:bg-green02 ${
                      isSubmitting || !isFormValid
                        ? 'bg-zinc-600 text-zinc-400 cursor-not-allowed'
                        : 'text-black hover:bg-green01'
                    }`}
                    style={{
                      minWidth: '70px',
                      height: '36px',
                    }}
                  >
                    <span>{isSubmitting ? t('common.saving') : t('common.save')}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeSettingsSection === 'UI Settings' && (
            <div className="flex-1 overflow-y-auto bg-black" style={{ minHeight: '0' }}>
              <UISettings
                hideExampleProjects={formData.hideExampleProjects}
                measurementPointsOnHover={formData.measurementPointsOnHover}
                onHideExampleProjectsChange={(value) =>
                  setFormData((prev) => ({ ...prev, hideExampleProjects: value }))
                }
                onMeasurementPointsOnHoverChange={(value) =>
                  setFormData((prev) => ({ ...prev, measurementPointsOnHover: value }))
                }
                muteNotificationSound={formData.muteNotificationSound}
                onMuteNotificationSoundChange={(value) =>
                  setFormData((prev) => ({ ...prev, muteNotificationSound: value }))
                }
              />

              <div className="flex h-[100px] p-8">
                <div className="">
                  <button
                    key={`edit-button`}
                    onClick={handleSubmit}
                    disabled={isSubmitting || !isFormValid}
                    className={`flex items-center justify-between px-6 py-3 rounded-full font-medium transition-colors duration-200 text-xs bg-green01 text-black hover:bg-green02 ${
                      isSubmitting || !isFormValid
                        ? 'bg-zinc-600 text-zinc-400 cursor-not-allowed'
                        : 'text-black hover:bg-green01'
                    }`}
                    style={{
                      minWidth: '70px',
                      height: '36px',
                    }}
                  >
                    <span>{isSubmitting ? t('common.saving') : t('common.save')}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeSettingsSection === 'Change password' && (
            <PasswordReset onSubmit={handlePasswordSubmit} isSubmitting={isSubmitting} />
          )}

          {activeSettingsSection === 'Billing' && (
            <BillingSettings onViewPlans={() => handleSettingsSectionChange('Plans & Subscriptions')} />
          )}
        </div>

        <Footer projectPage={false} statusBar={false} />
      </div>
    </div>
  );
};
